// Function to manage geocoding cache in localStorage
const geocodingCache = {
  get: (locationKey) => {
    try {
      const cached = localStorage.getItem(`geo_${locationKey}`);
      return cached ? JSON.parse(cached) : null;
    } catch (e) {
      console.warn("Error reading from geocoding cache:", e);
      return null;
    }
  },

  set: (locationKey, coordinates) => {
    try {
      localStorage.setItem(`geo_${locationKey}`, JSON.stringify(coordinates));
    } catch (e) {
      console.warn("Error writing to geocoding cache:", e);
    }
  },
};

// Helper to create consistent cache keys
const createLocationKey = (city, state) => {
  if (!city || !state) {
    return null;
  }
  return `${city?.toLowerCase().trim()}_${state?.toLowerCase().trim()}`;
};

// Calculate distance between two points using Haversine formula (in miles)
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  // Convert latitude and longitude from degrees to radians
  const toRad = (value) => (value * Math.PI) / 180;

  const R = 3958.8; // Earth's radius in miles
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;

  return distance;
};

// Function to calculate distance between two location objects with coordinates
const getDistanceBetweenLocations = (location1, location2) => {
  // Extract coordinates from first location
  let lat1, lon1;
  if (
    Array.isArray(location1.coordinates) &&
    location1.coordinates.length === 2
  ) {
    [lat1, lon1] = location1.coordinates;
  } else if (
    location1.coordinates &&
    typeof location1.coordinates === "object"
  ) {
    lat1 = parseFloat(location1.coordinates.latitude);
    lon1 = parseFloat(location1.coordinates.longitude);
  } else if (location1.lat !== undefined && location1.lng !== undefined) {
    lat1 = parseFloat(location1.lat);
    lon1 = parseFloat(location1.lng);
  } else {
    return null; // Can't calculate distance without coordinates
  }

  // Extract coordinates from second location
  let lat2, lon2;
  if (
    Array.isArray(location2.coordinates) &&
    location2.coordinates.length === 2
  ) {
    [lat2, lon2] = location2.coordinates;
  } else if (
    location2.coordinates &&
    typeof location2.coordinates === "object"
  ) {
    lat2 = parseFloat(location2.coordinates.latitude);
    lon2 = parseFloat(location2.coordinates.longitude);
  } else if (location2.lat !== undefined && location2.lng !== undefined) {
    lat2 = parseFloat(location2.lat);
    lon2 = parseFloat(location2.lng);
  } else {
    return null; // Can't calculate distance without coordinates
  }

  // Calculate and return the distance
  return calculateDistance(lat1, lon1, lat2, lon2);
};

// Function to find closest locations to a given location
const findClosestLocations = (
  targetLocation,
  candidateLocations,
  count = 3
) => {
  if (
    !targetLocation ||
    !candidateLocations ||
    candidateLocations.length === 0
  ) {
    return [];
  }

  // Calculate distance for each candidate location
  const locationsWithDistances = candidateLocations
    .map((location) => {
      const distance = getDistanceBetweenLocations(targetLocation, location);
      return {
        ...location,
        distance: distance !== null ? distance : Infinity,
      };
    })
    .filter((location) => location.distance !== Infinity);

  // Sort by distance and return the closest ones
  return locationsWithDistances
    .sort((a, b) => a.distance - b.distance)
    .slice(0, count);
};

// Function to geocode a batch of locations with caching
const batchGeocodeLocations = async (locations, onProgress) => {
  const results = [];
  const locationsToProcess = [];

  // First check for pre-defined coordinates or cache
  locations.forEach((location) => {
    // Check if location already has coordinates in the new format (object with latitude/longitude)
    if (
      location.coordinates &&
      typeof location.coordinates === "object" &&
      location.coordinates.latitude !== undefined &&
      location.coordinates.longitude !== undefined
    ) {
      // Convert to expected format [lat, lng]
      results.push({
        ...location,
        coordinates: [
          parseFloat(location.coordinates.latitude),
          parseFloat(location.coordinates.longitude),
        ],
      });
      onProgress && onProgress(results.length, locations.length);
    }
    // Check if location already has coordinates in the old array format
    else if (
      Array.isArray(location.coordinates) &&
      location.coordinates.length === 2
    ) {
      results.push({
        ...location,
        coordinates: location.coordinates,
      });
      onProgress && onProgress(results.length, locations.length);
    }
    // Check cache for city/state
    else {
      const locationKey = createLocationKey(location.city, location.state);
      const cachedCoords = geocodingCache.get(locationKey);

      if (cachedCoords) {
        results.push({
          ...location,
          coordinates: cachedCoords,
        });
        onProgress && onProgress(results.length, locations.length);
      } else {
        locationsToProcess.push(location);
      }
    }
  });

  // Process locations that don't have coordinates in parallel with rate limiting
  if (locationsToProcess.length > 0) {
    const batchSize = 2; // Number of simultaneous requests
    for (let i = 0; i < locationsToProcess.length; i += batchSize) {
      const batch = locationsToProcess.slice(i, i + batchSize);
      const batchPromises = batch.map(async (location) => {
        try {
          const query = `${location.city}, ${location.state}`;
          const response = await fetch(
            `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
              query
            )}&limit=1`,
            {
              headers: {
                "User-Agent": "FacilitiesMapApp/1.0",
              },
            }
          );

          if (!response.ok)
            throw new Error(`HTTP error! status: ${response.status}`);

          const data = await response.json();

          if (data.length > 0) {
            const coordinates = [
              parseFloat(data[0].lat),
              parseFloat(data[0].lon),
            ];
            const locationKey = createLocationKey(
              location.city,
              location.state
            );
            geocodingCache.set(locationKey, coordinates);

            return {
              ...location,
              coordinates,
            };
          }
          console.warn(`No coordinates found for ${query}`);
          return null;
        } catch (error) {
          console.error(
            `Error geocoding ${location.city}, ${location.state}:`,
            error
          );
          return null;
        }
      });

      // Wait for batch to complete
      const batchResults = await Promise.all(batchPromises);
      results.push(...batchResults.filter((r) => r !== null));
      onProgress && onProgress(results.length, locations.length);

      // Add delay between batches to respect rate limits
      if (i + batchSize < locationsToProcess.length) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
    }
  }

  return results;
};

export {
  batchGeocodeLocations,
  calculateDistance,
  getDistanceBetweenLocations,
  findClosestLocations,
};
